import React, { Fragment, useEffect } from "react";
import { identifierPairs, identifierParenthetical } from "../../config/statutoryIdentifiers";
import SEO from "../../components/SEO/SEO";
import { LegalPageLayout, LegalList, LegalListItem } from "../../components/LegalPageLayout";

/**
 * The document body, shared by this page and the signup TermsAcceptanceModal
 * so there is ONE copy of the text a customer reads and signs.
 */
export const PrivacyContent: React.FC = () => (
  <>
        <p>
          This Privacy Policy explains how <strong>Axoix Technologies Private Limited</strong> ("we", "us", "our") —
          an MSME-registered micro enterprise {identifierParenthetical()} with registered office at B-59,
          Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar,
          New Delhi, Delhi - 110059, India —
          collects, uses, and protects information when you use our corporate
          website <strong>axoix.in</strong>, our marketing site{" "}
          <strong>axoix.com</strong>, our tenant dashboards at{" "}
          <strong>*.axoix.io</strong>, and any related services
          (collectively, the "Services").
        </p>

        <h2 className="details-content-title mb-15 mt-50">1. Information We Collect</h2>
        <LegalList>
          <LegalListItem>
            <strong>Account data</strong> — name, email address, phone number, company name,
            and credentials you provide when signing up, requesting a quote, or applying as a
            contributor.
          </LegalListItem>
          <LegalListItem>
            <strong>Usage data</strong> — pages visited, features used, timestamps, IP
            address, device and browser information collected via server logs and analytics
            tooling.
          </LegalListItem>
          <LegalListItem>
            <strong>Tenant content</strong> — on AXOIX, any data you or your users upload
            (documents, emails, chats, voice recordings, images, etc.) is stored in your
            tenant's isolated database and object storage.
          </LegalListItem>
          <LegalListItem>
            <strong>Communications</strong> — messages you send us via forms, email
            (hello@axoix.com), or chat.
          </LegalListItem>
          <LegalListItem>
            <strong>GitHub identity</strong> — if you sign in with GitHub (e.g. as a
            contributor), we receive your GitHub handle, user id, avatar URL, name, and
            verified primary email via the GitHub App OAuth flow.
          </LegalListItem>
          <LegalListItem>
            <strong>Google account data</strong> — if you connect a Google account, we
            receive the account's email address, name, and profile picture, plus the
            Gmail, Calendar, Drive, Docs, Sheets, Google Ads, Analytics, YouTube,
            Business Profile or Classroom data covered by the scopes you approve. See{" "}
            <a href="#section-6">section 6</a> for the full scope-by-scope breakdown and
            our Limited Use commitment.
          </LegalListItem>
        </LegalList>

        <h2 className="details-content-title mb-15 mt-50">2. How We Use Your Information</h2>
        <LegalList>
          <LegalListItem>To provide, operate, and maintain the Services.</LegalListItem>
          <LegalListItem>To process signups, payments, invoicing, and tenant provisioning.</LegalListItem>
          <LegalListItem>To respond to enquiries, support requests, and contributor applications.</LegalListItem>
          <LegalListItem>To improve our products through aggregated, anonymised analytics.</LegalListItem>
          <LegalListItem>To send service-related emails (account notices, receipts, policy changes).</LegalListItem>
          <LegalListItem>To comply with applicable laws, including Indian tax and GST requirements.</LegalListItem>
        </LegalList>

        <h2 id="section-3" className="details-content-title mb-15 mt-50">
          3. AI Processing — Default vs. BYOK
        </h2>
        <p>
          <strong>By default, AXOIX runs on self-hosted AI infrastructure</strong> (fine-tuned
          local LLM, Whisper STT, Coqui TTS, image/video generation) on our own GPU server.
          Your tenant content is processed inside our servers, is not shared with any external
          AI provider, and is not used to train any shared or public model.
        </p>
        <p className="mt-20">
          <strong>Bring Your Own Key (BYOK) is an optional fallback.</strong> If you
          explicitly add an API key for a third-party AI provider via your account settings
          and route a request to that provider, then <strong>the prompt and any attached
          content you send — including audio sent for transcription and text sent for
          voice — will leave our servers and be processed by that third-party provider
          under their own terms and privacy policy</strong>. Providers currently supported:
          for text and chat, OpenAI, Anthropic, Google (Gemini), Groq, Mistral, DeepSeek,
          xAI (Grok), Perplexity, Cohere, Together AI, OpenRouter, Hugging Face and
          Replicate; for speech-to-text, Groq, OpenAI and Sarvam; for voice
          (text-to-speech), ElevenLabs and Sarvam; for images, Google (Gemini) and OpenAI. We encrypt your stored API key at rest but we do not
          control what happens to your content once it reaches the third-party provider.
          Enabling BYOK is your informed choice; you can deactivate or delete your BYOK key
          at any time from <em>Account → API Keys</em>.
        </p>
        <p className="mt-20">
          Examples of features where BYOK, when enabled, sends content out: AI chat, AI
          costing, AI content generation, AI image generation, voice transcription and
          voice generation. Features that never leave our servers regardless of BYOK:
          database queries, file storage and email IMAP/SMTP via our own mail server. Voice
          transcription runs on our Whisper instance and TTS on our Coqui instance unless
          you have added a BYOK key for a speech provider listed above.
        </p>

        <h2 className="details-content-title mb-15 mt-50">4. Sharing & Third Parties</h2>
        <p>
          We do not sell personal data. We share data only with the categories of service
          providers and integrations listed below, and only to the extent necessary to
          operate the Services. Each provider is subject to its own terms and privacy policy.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">Always involved (infrastructure)</h3>
        <LegalList>
          <LegalListItem>
            <strong>Backblaze B2</strong> — private per-tenant object storage for files you
            upload (documents, images, audio). Region: <code>eu-central-003</code>{" "}
            (Frankfurt, Germany). Tenant bucket credentials are encrypted at rest before
            being stored in your tenant database.
          </LegalListItem>
          <LegalListItem>
            <strong>Cloudflare</strong> — DNS, TLS, WAF, and CDN edge caching. File URLs
            served via <code>cdn.axoix.io</code> are validated with HMAC signatures, proxied
            through a Cloudflare Worker to Backblaze B2, and cached at Cloudflare edge
            locations globally so that repeat viewers are served from the nearest edge.
          </LegalListItem>
          <LegalListItem>
            <strong>Hostinger / VPS host</strong> — our dedicated virtual private server
            located in India hosts our application, databases, and email server.
          </LegalListItem>
        </LegalList>

        <h3 className="item_details_info_heading mb-20 mt-30">Used when you choose to use the feature</h3>
        <LegalList>
          <LegalListItem>
            <strong>Razorpay</strong> — payment processing. Receives only the billing
            information and payment details needed to complete the transaction.
          </LegalListItem>
          <LegalListItem>
            <strong>Brevo (Sendinblue)</strong> — transactional email delivery for platform
            notices sent by us to you (signup confirmation, password reset, receipts, service
            notices). Does not process email in your tenant mailboxes.
          </LegalListItem>
          <LegalListItem>
            <strong>GitHub</strong> — for contributors only, to verify identity via the
            GitHub App OAuth flow and manage repository invitations.
          </LegalListItem>
          <LegalListItem>
            <strong>Social media OAuth providers</strong> — Meta (Facebook, Instagram,
            Messenger, WhatsApp), LinkedIn, X (Twitter), Threads, and Telegram. Used only
            when you explicitly connect an account from your tenant dashboard (e.g. CHORUS
            social media posting). We receive the access tokens each provider issues you; we
            do not receive your social login password.
          </LegalListItem>
          <LegalListItem>
            <strong>Google</strong> — Gmail, Calendar, Drive, Docs, Sheets, Google Ads,
            Analytics, YouTube, Business Profile and Classroom, only when you connect a
            Google account yourself. See <a href="#section-6">section 6</a> for the exact
            scopes requested and how Google user data is handled.
          </LegalListItem>
          <LegalListItem>
            <strong>Telephony</strong> — self-hosted FusionPBX on our VPS handles SIP
            routing. Outbound calls and SMS leave our network via our upstream DID/SMS
            carriers (e.g. LINK DID). Call audio is never sent to any AI provider without
            your BYOK configuration.
          </LegalListItem>
          <LegalListItem>
            <strong>Third-party AI (BYOK)</strong> — the providers listed in section 3
            (OpenAI, Anthropic, Google, Groq and others). Only when you add a BYOK key and
            actively invoke a BYOK-routed feature. See section 3.
          </LegalListItem>
        </LegalList>

        <h3 className="item_details_info_heading mb-20 mt-30">Legal</h3>
        <LegalList>
          <LegalListItem>
            <strong>Legal obligations</strong> — when required by law, court order, or to
            protect our rights.
          </LegalListItem>
        </LegalList>

        <h2 id="section-5" className="details-content-title mb-15 mt-50">
          5. MCP Connector — Connecting Claude &amp; Other AI Assistants to AXOIX
        </h2>
        <p>
          AXOIX offers an optional remote <strong>MCP (Model Context Protocol)</strong> connector
          at <code>mcp.axoix.io</code> that lets you operate your AXOIX tenant from an AI
          assistant such as Claude, ChatGPT, or Cursor — for example, checking bookings,
          summarising invoices, or reviewing leads by chatting with the assistant instead of
          logging into the dashboard. This section explains what happens to your data if you
          choose to connect it. <strong>The MCP connector is off by default</strong> and only
          activates when you explicitly authorise a specific AI assistant from your tenant
          settings.
        </p>
        <LegalList>
          <LegalListItem>
            <strong>You authorise the connection yourself.</strong> Connecting an assistant
            uses the OAuth 2.1 standard (with PKCE): you log in with your own AXOIX credentials
            on an AXOIX-hosted consent page and approve exactly which scopes the assistant may
            use (e.g. read-only, CRM, finance, Chorus content). The assistant provider (e.g.
            Anthropic) never sees your AXOIX password.
          </LegalListItem>
          <LegalListItem>
            <strong>The connector is a proxy, not a copy of your data.</strong>{" "}
            <code>mcp.axoix.io</code> does not maintain its own database of your tenant content.
            Each tool call from the assistant is translated into a request against our existing
            tenant API, authenticated with a token scoped to your account and role, and the
            response is returned to the assistant in that moment. Your business data is not
            additionally stored, cached, or retained by the MCP gateway itself.
          </LegalListItem>
          <LegalListItem>
            <strong>Only the data you ask about is shared with the assistant.</strong> When you
            ask the assistant a question or give it an instruction, the specific tenant data
            needed to answer — and nothing else — is sent to that assistant's provider so it can
            generate a response, in the same way it would be if you pasted that information into
            a chat yourself. That exchange is governed by the AI assistant provider's own
            privacy policy and terms once the data reaches them.
          </LegalListItem>
          <LegalListItem>
            <strong>Actions that change data require your confirmation.</strong> Read requests
            (e.g. "show me this week's occupancy") run directly. Actions that create or modify
            records (e.g. sending an invoice reminder, updating a lead) are flagged to the
            assistant as requiring your explicit confirmation before they execute.
          </LegalListItem>
          <LegalListItem>
            <strong>Every call is logged.</strong> We keep an audit log of MCP tool calls made
            against your tenant (tool name, timestamp, and result status) so you can review what
            an assistant did on your behalf. Sensitive values (passwords, OTPs, tokens, card
            numbers) are redacted from audit records before they are stored.
          </LegalListItem>
          <LegalListItem>
            <strong>Tenant isolation is enforced.</strong> A connected assistant can only ever
            act as the tenant user who authorised it — it cannot access another tenant's data
            under any circumstances.
          </LegalListItem>
          <LegalListItem>
            <strong>You can revoke access at any time</strong> from{" "}
            <em>Account → Connected Apps</em>, which immediately invalidates the assistant's
            access to your tenant.
          </LegalListItem>
        </LegalList>

        <h2 id="section-6" className="details-content-title mb-15 mt-50">
          6. Google User Data
        </h2>
        <p>
          This section is the complete statement of how AXOIX requests, uses, stores and
          deletes Google user data, and it governs every Google service listed below.
          AXOIX&rsquo;s use of information received from Google APIs adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">6.1 Connecting is opt-in, and granted one service at a time</h3>
        <p>
          Nothing is requested until you start the connection yourself and approve it on
          Google&rsquo;s own consent screen. Connecting your Google account initially grants
          sign-in only, and every other capability is a separate, later consent. You can hold
          any combination of the services below, or none of them, and the rest of AXOIX works
          either way.
        </p>
        <p>
          Two details about how those later consents work, because they affect what you see at
          Google and what you need to do to withdraw access:
        </p>
        <LegalList>
          <LegalListItem>
            <strong>YouTube is a separate connection.</strong> Since 19 August 2026 YouTube is
            served by its own Google OAuth client, distinct from the one behind Calendar,
            Drive, Docs, Sheets, Ads, Analytics, Business Profile and Classroom. Both clients
            belong to the same Google Cloud project and share one consent screen, so the
            permissions we ask for are unchanged &mdash; but your Google account now holds{" "}
            <strong>two independent grants</strong> with us rather than one. Section 6.8
            explains what that means when you revoke.
          </LegalListItem>
          <LegalListItem>
            <strong>A capability may sit on a different Google account.</strong> You can assign
            services to different Google accounts &mdash; for example sign-in and Calendar on
            your work account and YouTube on the account that owns the channel. A later consent
            is therefore not necessarily an addition to an existing connection; it may create a
            new one.
          </LegalListItem>
        </LegalList>
        <p>
          For the non-YouTube services you are not signed out to add a capability, and are not
          asked to re-approve one you already granted. <strong>YouTube is different:</strong>{" "}
          its consent carries all six of its permissions in a single request, so approving it
          to reach one screen also approves the rest &mdash; opening the Revenue screen
          re-approves upload, comment moderation and analytics alongside it.
        </p>
        <p>
          Two services are available only to particular customers:{" "}
          <strong>Google Classroom</strong> is offered only to organisations running our
          Education module, and this is enforced on our servers, not merely hidden in the
          interface. <strong>Google Ads</strong> additionally requires a developer token
          issued to us by Google.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">6.2 Every scope we request, and why</h3>
        <p>
          These are the exact permission strings shown on Google&rsquo;s consent screen. We
          request nothing outside this list &mdash; twenty-three permissions across ten
          services.
        </p>
        <LegalList>
          <LegalListItem>
            <strong>Sign-in</strong> &mdash; <code>openid</code>, <code>userinfo.email</code>,{" "}
            <code>userinfo.profile</code>. To identify which Google account is connected and
            display its name, email address and avatar.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Calendar</strong> &mdash; <code>calendar</code>. To read the events
            in your calendar, to write the bookings and class sessions you create in AXOIX
            back into it, and to attach a Google Meet link where the booking is a meeting.
            AXOIX reads your calendar list and creates, updates and deletes events &mdash;
            including events it did not create, when you delete them from the AXOIX calendar.
            It does not create or delete calendars.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Drive (AXOIX files only)</strong> &mdash; <code>drive.file</code>.
            AXOIX can only ever see files it created itself or that you explicitly hand to it
            through the Google Picker. It cannot list, search or open anything else in your
            Drive.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Docs</strong> &mdash; <code>drive.file</code>. To create and
            edit the Google Docs that AXOIX generates for you, such as proposals, contracts
            and HR letters, and any document you hand to AXOIX yourself through the Google
            Picker. We no longer request <code>documents</code>, which would have meant
            access to every Google Doc in your account; AXOIX can reach a document only if
            it created it or you picked it.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Sheets</strong> &mdash; <code>drive.file</code>. To create and
            fill the Google Sheets that AXOIX generates when you export a report or table,
            and any workbook you hand to AXOIX yourself through the Google Picker. We no
            longer request <code>spreadsheets</code>, which would have meant access to every
            Google Sheet in your account.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Ads</strong> &mdash; <code>adwords</code>. To read the performance
            of the Google Ads accounts you connect, and to create and manage campaigns, ad
            groups, keywords, ads, budgets, bids, targeting and conversion actions. Google
            publishes no read-only variant of this permission, so a customer who only wants
            reporting grants the same scope as one who makes changes; who can actually change
            anything is decided by role inside AXOIX, and that is enforced on our servers.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Analytics</strong> &mdash; <code>analytics.readonly</code>,{" "}
            <code>analytics.edit</code>, <code>analytics.manage.users</code>. To read your GA4
            reports, to administer properties, data streams and custom dimensions, and to
            manage who has access to the account.
          </LegalListItem>
          <LegalListItem>
            <strong>YouTube</strong> &mdash; <code>youtube.readonly</code>,{" "}
            <code>youtube.upload</code>, <code>youtube.force-ssl</code>,{" "}
            <code>yt-analytics.readonly</code>, <code>yt-analytics-monetary.readonly</code>,{" "}
            <code>youtube.channel-memberships.creator</code>. To upload the videos you publish
            from CHORUS; to read your channel name, avatar and subscriber and view counts; to
            edit, delete and thumbnail your uploads and to read, reply to, moderate and delete
            comments on your channel; to show watch time, audience retention and traffic
            sources; to show your channel&rsquo;s earnings, CPM, ad impressions and monetised
            playbacks; and to list your channel members, their tier and how long they have
            been a member. The two analytics permissions come from the YouTube Analytics API,
            a separate service whose figures cannot be derived from the Data API. Since
            19 August 2026 a single YouTube consent covers all six of these permissions at
            once &mdash; they are not requested one screen at a time. A channel connected
            before that date keeps only the permissions it was granted then, and shows no
            Revenue or Members screen until you reconnect it, which grants the full set.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Business Profile</strong> &mdash; <code>business.manage</code>. To
            manage the business listing you connect in AXOIX &mdash; its details and hours,
            reviews and replies, posts, questions and answers, and the Search and Maps
            performance behind them.
          </LegalListItem>
          <LegalListItem>
            <strong>Google Classroom</strong> (Education customers only) &mdash;{" "}
            <code>classroom.courses</code>, <code>classroom.rosters</code>,{" "}
            <code>classroom.coursework.students</code>, <code>classroom.announcements</code>,{" "}
            <code>classroom.topics</code>, <code>classroom.profile.emails</code>,{" "}
            <code>classroom.courseworkmaterials</code>. To synchronise courses, rosters,
            coursework, materials, submissions, grades and announcements in both directions
            with the AXOIX Education module. <code>classroom.profile.emails</code> is what
            allows a Classroom course member to be matched to the corresponding student record
            in AXOIX.
          </LegalListItem>
        </LegalList>

        <p className="mt-20">
          <strong>Permissions we deliberately do not hold.</strong>
        </p>
        <LegalList>
          <LegalListItem>
            <strong>No Gmail permission of any kind.</strong> AXOIX does not request{" "}
            <code>gmail.modify</code>, <code>gmail.send</code>, <code>gmail.readonly</code>,{" "}
            <code>gmail.metadata</code>, <code>gmail.compose</code>, <code>gmail.labels</code>{" "}
            or <code>mail.google.com</code>. No part of AXOIX can read, send, label or delete
            mail in your Gmail mailbox, and no Google connection you make with us can be
            granted the ability to. The mail features inside AXOIX run on mailboxes we host
            ourselves on our own mail server; they are unrelated to your Google account.
          </LegalListItem>
          <LegalListItem>
            <strong>No permission to browse your Drive.</strong> <code>drive</code>,{" "}
            <code>drive.readonly</code> and <code>drive.metadata.readonly</code> are not
            requested. Everything AXOIX generates for you &mdash; proposals, contracts,
            letters, exported reports and spreadsheets &mdash; works under{" "}
            <code>drive.file</code>, which can only ever see what AXOIX created or what you
            handed to it.
          </LegalListItem>
          <LegalListItem>
            <strong>No permission to open your existing Docs or Sheets.</strong>{" "}
            <code>documents</code> and <code>spreadsheets</code> &mdash; each of which means
            every Google Doc or every Google Sheet in your account &mdash; were withdrawn
            from our Google client on 9 September 2026 and can no longer be granted on any
            AXOIX connection. Both the Docs and the Sheets features run on{" "}
            <code>drive.file</code> instead, so AXOIX reaches a file only when it created
            that file or you chose it yourself in the Google Picker.
          </LegalListItem>
          <LegalListItem>
            <strong>No YouTube partner or content-owner permission.</strong>{" "}
            <code>youtube</code> (the broad variant), <code>youtubepartner</code> and{" "}
            <code>youtubepartner-channel-audit</code> are not requested. The per-feature
            permissions listed above cover everything CHORUS does.
          </LegalListItem>
        </LegalList>

        <h3 className="item_details_info_heading mb-20 mt-30">6.3 We request no &ldquo;restricted&rdquo; permission</h3>
        <p>
          Google classifies permissions as non-sensitive, sensitive or{" "}
          <strong>restricted</strong>. Restricted is the highest tier and is reserved for
          permissions that expose the whole of a mailbox or the whole of a Drive.
        </p>
        <p>
          <strong>AXOIX requests no restricted permission.</strong> Every permission in
          section 6.2 is non-sensitive or sensitive.
        </p>
        <p>
          Earlier versions of this policy described two restricted permissions &mdash;{" "}
          <code>gmail.modify</code>, for a full Gmail mailbox inside AXOIX, and{" "}
          <code>drive</code>, for browsing your whole Drive. Both have been withdrawn from our
          Google client and neither can be granted on any AXOIX connection today. The Gmail
          feature is switched off in the product entirely; Drive browsing continues on{" "}
          <code>drive.file</code> and simply lists only the files AXOIX created or that you
          handed to it.
        </p>
        <p>
          If we ever reinstate either of them, this page changes first, they go through
          Google&rsquo;s restricted-scope verification and an independent Cloud Application
          Security Assessment (CASA), and you would still be asked to approve the new
          permission on Google&rsquo;s own consent screen before anything was granted.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">6.4 Limited Use &mdash; how we use Google user data</h3>
        <p>
          Google user data is used solely to provide the feature you enabled. Specifically,
          it is:
        </p>
        <LegalList>
          <LegalListItem>
            <strong>Never sold</strong>, licensed, rented or transferred to a data broker or
            any other party.
          </LegalListItem>
          <LegalListItem>
            <strong>Never used for advertising</strong>, ad personalisation, retargeting,
            audience building or measurement of any kind.
          </LegalListItem>
          <LegalListItem>
            <strong>Never used to develop, improve or train</strong> generalised AI or
            machine-learning models &mdash; including the self-hosted models described in
            section 3.
          </LegalListItem>
          <LegalListItem>
            <strong>Never sent to a third-party AI provider.</strong> Google user data is
            excluded from Bring-Your-Own-Key routing, so it is not forwarded to an external
            model provider &mdash; including any of the BYOK providers listed in section 3
            &mdash; even if you have configured a BYOK key there.
          </LegalListItem>
          <LegalListItem>
            <strong>Never aggregated across customers</strong>, benchmarked, warehoused for
            secondary use, or resold in derived form. Google Ads, Analytics and YouTube
            performance data is fetched on demand to display to your own authorised users.
          </LegalListItem>
          <LegalListItem>
            <strong>Never read by AXOIX personnel</strong>, except with your explicit consent
            (for example when you raise a support request), where strictly necessary for
            security purposes, or where required by law.
          </LegalListItem>
        </LegalList>

        <h3 className="item_details_info_heading mb-20 mt-30">6.5 Where tokens and data are stored</h3>
        <p>
          The access and refresh tokens Google issues are transmitted only over TLS and are
          never returned to your browser or exposed in any AXOIX API response. Where they rest
          depends on which service you connected:
        </p>
        <p>
          You may connect <strong>more than one Google account</strong>, and assign different
          services to different accounts. Each connected account holds its own set of tokens,
          stored and encrypted separately from the others, and disconnecting one leaves the
          rest untouched. Where this section refers to a connected account it means each of
          them individually, not a single account per user.
        </p>
        <LegalList>
          <LegalListItem>
            <strong>Sign-in, Calendar, Drive, Docs, Sheets, Ads, Analytics, Business Profile
            and Classroom</strong> &mdash; tokens are stored only in your own tenant&rsquo;s
            isolated database, against your individual user record, and are encrypted at rest
            with authenticated encryption (Fernet &mdash; AES-128-CBC with HMAC-SHA256, from a
            key derived with PBKDF2-HMAC-SHA256 at 100,000 iterations).
          </LegalListItem>
          <LegalListItem>
            <strong>YouTube</strong> &mdash; because CHORUS publishing runs from our central
            platform rather than inside your tenant, this token is stored in our central AXOIX
            platform database, tagged to your tenant and project, and encrypted at rest. It is
            used only to publish to, and report on, the channel you connected.
          </LegalListItem>
        </LegalList>
        <p>
          We do not create a copy of your Drive or ad account. File content, ad performance
          and analytics are fetched from Google when you open the screen that displays them.
          What AXOIX stores persistently is limited to what a feature genuinely needs: the
          identity of the connected account, the set of permissions granted, a record linking
          a Drive file you chose to the AXOIX record you attached it to, a record of AXOIX
          bookings that have been written into your calendar so they can be kept in step, and
          &mdash; for Education customers &mdash; the Classroom courses, rosters, coursework
          and grades being synchronised with your Education module.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">6.6 How long we keep it, and the one copy that leaves our servers</h3>
        <LegalList>
          <LegalListItem>
            <strong>Tokens</strong> are kept only while the connection exists. Disconnecting
            deletes them in the same request; we do not archive an expired or revoked token.
          </LegalListItem>
          <LegalListItem>
            <strong>Records derived from a Google service</strong> &mdash; the link between a
            Drive file and the AXOIX record you attached it to, the record of which AXOIX
            bookings have been written into your calendar, and, for Education customers, the
            Classroom data being synchronised &mdash; are kept for as long as the AXOIX record
            they belong to, because deleting them would break the AXOIX feature rather than
            protect you. They go when you ask us to remove them, and when you delete your
            account.
          </LegalListItem>
          <LegalListItem>
            <strong>File content is not retained at all.</strong> It is fetched from Google
            each time you open the screen that shows it and is not written to our database.
          </LegalListItem>
        </LegalList>
        <p>
          <strong>Backups</strong> &mdash; stated plainly, because it is the one place a copy
          of Google user data exists outside our own machines. Every database is dumped
          nightly and encrypted with AES-256 before it goes anywhere. Because your Google
          tokens and the derived records above live inside your tenant database, they are
          inside that encrypted dump. Whether the dump then leaves our servers follows the
          same rule as the rest of your data in section 9: on a paid plan only with your
          cloud-storage consent, and on the free plan always, as a stated condition of that
          plan. The one exception is our central platform database, which holds the YouTube
          token and is always included, because it is what a recovery of the platform itself
          depends on.
        </p>
        <p>
          The off-site copy is held by Backblaze B2 in the EU (eu-central-003, Frankfurt) for
          seven days, and on our own servers for thirty. Backblaze stores ciphertext and holds
          no key: the encryption key is kept in our secret store and is deliberately never
          written into the same bucket as the backups it opens. A storage provider holding an
          encrypted copy on our behalf is not a transfer of your Google data to anyone for any
          other purpose, and the Limited Use commitments in section 6.4 apply to it unchanged.
        </p>

        <h3 className="item_details_info_heading mb-20 mt-30">6.7 What cannot reach your Google data</h3>
        <LegalList>
          <LegalListItem>
            <strong>Third-party AI providers.</strong> Google user data is excluded from
            Bring-Your-Own-Key routing (section 3). Configuring a key for any BYOK provider
            does not put your files, calendar or Classroom records in front of that
            provider.
          </LegalListItem>
          <LegalListItem>
            <strong>The MCP connector.</strong> The AI-assistant connector described in
            section 5 works from a fixed catalogue of AXOIX operations that is closed by
            default &mdash; an operation is unreachable unless it has been explicitly added
            &mdash; and no Google-backed operation is in it. An assistant you connect cannot
            read your Drive, Calendar or Classroom data through AXOIX.
          </LegalListItem>
          <LegalListItem>
            <strong>Our other integrations.</strong> Google user data is never sent to
            Razorpay, Brevo, the social-media platforms, our travel or e-commerce providers,
            or our telephony carriers. Those providers appear in section 4 for their own
            purposes and receive nothing from a Google connection.
          </LegalListItem>
        </LegalList>

        <h3 className="item_details_info_heading mb-20 mt-30">6.8 Revoking access and deletion</h3>
        <p>
          You can disconnect at any time, and you can revoke our access directly at{" "}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
          >
            myaccount.google.com/permissions
          </a>
          .
        </p>
        <div className="postabmin_block xb-border mt-20 mb-20">
          <div className="admin_content">
            <p>
              <strong>
                AXOIX appears twice on that page, and removing one does not remove the other.
              </strong>{" "}
              Since 19 August 2026 YouTube is served by its own Google OAuth client (section
              6.1), so a Google account that has connected both holds two separate grants and
              Google lists them as two separate entries. One entry covers{" "}
              <strong>YouTube</strong>. The other covers{" "}
              <strong>
                sign-in, Calendar, Drive, Docs, Sheets, Ads, Analytics, Business Profile and
                Classroom
              </strong>
              . Removing either one revokes only the services it covers and leaves the other
              fully live. <strong>To withdraw all access, remove both entries.</strong> If you
              have connected more than one Google account, repeat this on each of them.
            </p>
          </div>
        </div>
        <LegalList>
          <LegalListItem>
            <strong>Sign-in, Calendar, Drive, Docs, Sheets, Ads, Analytics, Business Profile
            and Classroom</strong> &mdash; disconnecting from Settings &rarr; Google Workspace
            sends a revocation request to Google and deletes the stored access and refresh
            tokens immediately.
          </LegalListItem>
          <LegalListItem>
            <strong>YouTube</strong> &mdash; disconnecting from CHORUS sends a revocation
            request to Google for the underlying grant and deletes the stored account record
            and its token from our platform database.
          </LegalListItem>
        </LegalList>
        <p>
          Because the two grants are independent, each of these disconnects now affects only
          the services named in it &mdash; disconnecting YouTube leaves Calendar, Drive and
          Classroom working, and disconnecting the others leaves a connected channel
          publishing. Revoking a grant stops all future access to the services that grant
          covers, immediately; it does not touch the other grant. To also have any Google content
          AXOIX retained on your behalf removed &mdash; synchronised Classroom records,
          calendar sync records, or Drive file links &mdash; email{" "}
          <a href="mailto:privacy@axoix.com">privacy@axoix.com</a> and we will delete it within
          30 days. We will not claim to edit a backup that has already been written: encrypted
          dumps taken before your request are not rewritten, they expire on the schedule in
          section 6.6 &mdash; seven days off-site, thirty on our own servers &mdash; and
          nothing is ever restored from them except to recover the platform after a failure.
          Deleting your AXOIX account removes all of it, as described in section 9 and on our
          data deletion page.
        </p>
        <p>
          AXOIX&rsquo;s use and transfer of information received from Google APIs to any other
          app will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h2 className="details-content-title mb-15 mt-50">7. Where Your Data Lives</h2>
        <LegalList>
          <LegalListItem>
            <strong>Compute, databases, email server</strong> — our VPS in India.
          </LegalListItem>
          <LegalListItem>
            <strong>Object storage (uploaded files)</strong> — Backblaze B2{" "}
            <code>eu-central-003</code> region (Frankfurt, Germany). Each tenant has a
            dedicated private bucket.
          </LegalListItem>
          <LegalListItem>
            <strong>CDN edge cache</strong> — Cloudflare globally distributed edge servers
            cache publicly-accessed file responses (signed URLs) transiently to reduce
            latency and bandwidth cost. Cached copies are purged on a rolling TTL; you can
            request a purge at any time.
          </LegalListItem>
          <LegalListItem>
            <strong>BYOK AI responses</strong> — if enabled, processed by the chosen
            provider in their region (see their documentation).
          </LegalListItem>
        </LegalList>
        <p className="mt-20">
          If your jurisdiction (e.g. EU GDPR, UK GDPR, DPDP Act India) imposes specific data
          residency or cross-border transfer obligations on you as a data controller, you are
          responsible for assessing whether this architecture meets those obligations before
          using AXOIX.
        </p>

        <h2 className="details-content-title mb-15 mt-50">8. Data Security</h2>
        <p>
          Credentials and secrets are stored in HashiCorp Vault. Tenant B2 storage
          credentials and mailbox passwords stored in your tenant database are encrypted
          with <strong>Fernet</strong> (AES-128-CBC with HMAC-SHA256 authentication), using
          a key derived via{" "}
          <strong>PBKDF2HMAC-SHA256 with 100,000 iterations</strong>. All public traffic
          uses HTTPS with TLS certificates from Let's Encrypt. We restrict administrative
          access on a least-privilege basis. No system is impenetrable, but we take
          reasonable steps to protect your data against unauthorised access.
        </p>

        <h2 className="details-content-title mb-15 mt-50">9. Data Retention &amp; Backups</h2>
        <p>
          We retain account and tenant data for as long as your account is active. After
          account closure we retain minimum records required by law (e.g. invoices, tax
          records) and otherwise delete tenant content on request. Contact{" "}
          <a href="mailto:hello@axoix.com">hello@axoix.com</a> to request deletion.
          Note that deletion of tenant content includes the contents of your Backblaze B2
          bucket; Cloudflare edge caches for your files are purged on request within 24
          hours.
        </p>
        <p className="mt-20">
          <strong>Off-site backups and cloud-storage consent.</strong> Whether a copy of
          your data leaves our own servers depends on your plan, and on paid plans it is
          your choice:
        </p>
        <LegalList>
          <LegalListItem>
            <strong>Paid plans — you decide.</strong> We hold an off-site copy in Backblaze
            B2 (EU) only if you have given us cloud-storage consent. Without that consent
            your data is backed up on our own servers only and never reaches a third-party
            storage provider. You can withdraw the consent at any time from your dashboard,
            and we stop sending anything further immediately. Copies already stored are{" "}
            <strong>not deleted automatically</strong> — withdrawing consent is not a
            deletion request, and destroying files because you changed your mind about where
            they live could lose you data. Ask us and we will erase them, or migrate them
            back to our servers first; either way we action it without delay.
          </LegalListItem>
          <LegalListItem>
            <strong>Free plan — included, and not optional.</strong> Free accounts are
            closed automatically after 40 days of inactivity, so the off-site copy is the
            only thing that makes an unintended closure reversible. For a free account the
            realistic alternative is not &ldquo;stays on our servers&rdquo; but
            &ldquo;permanently destroyed&rdquo;, so it is a condition of the free plan rather
            than a choice. Upgrading to any paid plan gives you the control described above.
          </LegalListItem>
        </LegalList>

        <h2 className="details-content-title mb-15 mt-50">10. Your Rights</h2>
        <p>
          Subject to applicable law, you may: request a copy of the data we hold about you;
          correct inaccurate data; request erasure; object to processing; or withdraw
          consent where processing is based on consent. Email{" "}
          <a href="mailto:hello@axoix.com">hello@axoix.com</a> to exercise any
          right.
        </p>

        <h2 className="details-content-title mb-15 mt-50">11. Cookies</h2>
        <p>
          We use a minimal set of cookies for authentication sessions and basic analytics.
          You can control cookies via your browser settings; disabling cookies may impair
          parts of the Services that require sign-in.
        </p>

        <h2 className="details-content-title mb-15 mt-50">12. Children</h2>
        <p>
          The Services are not directed to individuals under 18. We do not knowingly collect
          data from children. If you believe a child has provided us data, contact us and we
          will delete it.
        </p>

        <h2 className="details-content-title mb-15 mt-50">13. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          notified by email or a notice on the Services. Continued use after changes means
          you accept the revised policy.
        </p>

        <h2 className="details-content-title mb-15 mt-50">14. Contact</h2>
        <p>
          Questions, complaints, or requests? Email{" "}
          <a href="mailto:hello@axoix.com">hello@axoix.com</a> or call{" "}
          <a href="tel:+918929222212">+91 89292 22212</a>.
        </p>
        <p className="mt-20">
          <strong>Registered office:</strong> Axoix Technologies Private Limited, B-59,
          Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar, New Delhi,
          Delhi - 110059, India.<br />
          {identifierPairs().map(([label, value], i) => (
            <React.Fragment key={label}>
              {i > 0 && <> &nbsp;|&nbsp; </>}
              <strong>{label}:</strong> {value}
            </React.Fragment>
          ))}
        </p>
        <p className="mt-20">
          See also our <a href="/terms">Terms of Service</a>.
        </p>
      </>
);

const PrivacyPage: React.FC = () => {
  return (
    <Fragment>
      <SEO
        title="Privacy Policy"
        description="AXOIX Privacy Policy — how Axoix Technologies Private Limited collects, uses, and protects your data across axoix.in, axoix.com, and *.axoix.io tenant dashboards."
        path="/privacy"
      />
      <LegalPageLayout
        pageTitle="Privacy Policy"
        pageSub="Privacy"
        lastUpdated="October 3, 2026"
      >
        <PrivacyContent />
      </LegalPageLayout>
    </Fragment>
  );
};

export default PrivacyPage;
